<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header liga-float-header showcase-page-header">
      <ion-toolbar class="showcase-float-toolbar">
        <div class="showcase-float-bar liga-float-bar">
          <button
            v-if="showInlineMenuButton"
            type="button"
            class="liga-float-circle showcase-float-btn"
            aria-label="Открыть меню"
            @click="openMainMenu"
          >
            <ion-icon :icon="menuOutline" />
          </button>
          <div v-else class="liga-float-spacer" aria-hidden="true" />

          <div class="liga-float-title-pill showcase-title-pill">
            <span class="liga-float-title-text">Национальная Лига Тренеров</span>
            <small class="liga-float-title-sub">Подписки · События · Релизы · Тренинги</small>
          </div>

          <button
            v-if="authenticated"
            type="button"
            class="liga-float-circle showcase-float-btn"
            aria-label="Liga.Chat"
            @click="goMessenger"
          >
            <span class="ios-unread-icon-wrap">
              <ion-icon :icon="chatbubbleEllipsesOutline" />
              <span v-if="messengerUnreadLabel" class="ios-unread-badge ios-unread-badge--on-icon">{{ messengerUnreadLabel }}</span>
            </span>
          </button>
          <button
            v-else
            type="button"
            class="liga-float-circle showcase-float-btn"
            aria-label="Вход"
            @click="goLogin"
          >
            <ion-icon :icon="logInOutline" />
          </button>
        </div>
      </ion-toolbar>
    </ion-header>

    <ion-content
      class="main-background showcase-content"
      :fullscreen="true"
    >
      <div v-if="loadError" class="showcase-state showcase-error">{{ loadError }}</div>
      <div v-else-if="loading" class="showcase-state showcase-loading-wrap">
        <ion-spinner name="crescent" class="showcase-loading-spinner" />
        <p class="showcase-loading-text">Загрузка витрины…</p>
      </div>

      <template v-else>
        <section class="showcase-section showcase-section--first">
          <div class="showcase-section-head">
            <h2 class="showcase-section-title">Подписки</h2>
          </div>
          <p v-if="subscriptions.length === 0" class="showcase-empty">Нет доступных подписок</p>
          <div v-else class="showcase-scroll-wrap">
            <div class="showcase-scroll">
              <article
                v-for="sub in subscriptions"
                :key="'sub-' + sub.id"
                class="showcase-event-card showcase-sub-card"
                @click="onSubscriptionCardClick(sub)"
              >
                <div
                  class="showcase-event-image-wrap"
                  :class="{ 'showcase-event-image-wrap--no-logo': !sub.logoPath }"
                >
                  <img
                    v-if="sub.logoPath"
                    :src="subscriptionsService.logoUrl(sub.logoPath, 480)"
                    :alt="sub.title || ''"
                    class="showcase-event-img"
                  />
                  <div
                    class="showcase-event-overlay"
                    :class="{ 'showcase-event-overlay--no-logo': !sub.logoPath }"
                  >
                    <h3 class="showcase-event-title">{{ sub.title }}</h3>
                    <p v-if="sub.durationDays" class="showcase-event-meta">{{ sub.durationDays }} {{ durationLabel(sub.durationDays) }}</p>
                    <div class="showcase-event-badges">
                      <span v-if="isSubscriptionPaid(sub)" class="showcase-badge showcase-badge--paid">Платная</span>
                      <span v-if="isSubscribed(sub)" class="showcase-badge showcase-badge--registered">Активна</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="isSubscriptionPaid(sub) && !isSubscribed(sub)"
                  class="showcase-event-actions"
                  @click.stop
                >
                  <ion-button
                    expand="block"
                    class="showcase-buy-btn showcase-accent-btn"
                    fill="solid"
                    shape="round"
                    size="small"
                    @click="onPaidSubscriptionAction(sub)"
                  >
                    Оформить
                  </ion-button>
                </div>
                <div
                  v-else-if="isSubscriptionPaid(sub) && isSubscribed(sub) && canRenewSubscription(sub)"
                  class="showcase-event-actions"
                  @click.stop
                >
                  <ion-button
                    expand="block"
                    class="showcase-buy-btn showcase-accent-btn"
                    fill="outline"
                    shape="round"
                    size="small"
                    @click="onPaidSubscriptionAction(sub)"
                  >
                    Продлить
                  </ion-button>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section class="showcase-section">
          <div class="showcase-section-head">
            <h2 class="showcase-section-title">События</h2>
          </div>
          <p v-if="events.length === 0" class="showcase-empty">Нет предстоящих событий</p>
          <div v-else class="showcase-scroll-wrap">
            <div class="showcase-scroll">
              <article
                v-for="ev in events"
                :key="'ev-' + ev.id"
                class="showcase-event-card"
                @click="onEventCardClick(ev)"
              >
                <div
                  class="showcase-event-image-wrap"
                  :class="{ 'showcase-event-image-wrap--no-logo': !ev.logoPath }"
                >
                  <img
                    v-if="ev.logoPath"
                    :src="eventsService.logoUrl(ev.logoPath, 480)"
                    :alt="ev.title || ''"
                    class="showcase-event-img"
                  />
                  <div
                    class="showcase-event-overlay"
                    :class="{ 'showcase-event-overlay--no-logo': !ev.logoPath }"
                  >
                    <h3 class="showcase-event-title">{{ ev.title }}</h3>
                    <p v-if="eventDateMeta(ev)" class="showcase-event-meta">{{ eventDateMeta(ev) }}</p>
                    <p class="showcase-event-loc">{{ ev.location || 'Место уточняется' }}</p>
                    <div class="showcase-event-badges">
                      <span v-if="isEventPaid(ev)" class="showcase-badge showcase-badge--paid">Платное</span>
                      <span v-if="isSoldOut(ev)" class="showcase-badge showcase-badge--soldout">Sold out</span>
                      <span v-if="isRegistered(ev)" class="showcase-badge showcase-badge--registered">Записан</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="isEventPaid(ev) && !isSoldOut(ev) && !isRegistered(ev)"
                  class="showcase-event-actions"
                  @click.stop
                >
                  <ion-button
                    expand="block"
                    class="showcase-buy-btn showcase-accent-btn"
                    fill="solid"
                    shape="round"
                    size="small"
                    @click="onPaidEventAction(ev)"
                  >
                    Оформить участие
                  </ion-button>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section class="showcase-section">
          <div class="showcase-section-head">
            <h2 class="showcase-section-title">Релизы от Лиги</h2>
          </div>
          <p v-if="programs.length === 0" class="showcase-empty">Пока нет программ</p>
          <div v-else class="showcase-scroll-wrap">
            <div class="showcase-scroll">
              <button
                v-for="p in programs"
                :key="'prog-' + p.id"
                type="button"
                class="showcase-tile showcase-tile--program"
                @click="onProgramClick(p)"
              >
                <div class="showcase-program-cover">
                  <img :src="programCoverUrl(p.shortName)" :alt="p.programName || ''" class="showcase-cover-img" />
                </div>
                <span class="showcase-tile-title">{{ p.programName }}</span>
                <span class="showcase-release-count">{{ releaseCount(p) }} {{ releaseCountLabel(releaseCount(p)) }}</span>
              </button>
            </div>
          </div>
        </section>

        <section class="showcase-section showcase-section--last">
          <div class="showcase-section-head">
            <h2 class="showcase-section-title">Тренинги</h2>
          </div>
          <p v-if="educationSectionsVisible.length === 0" class="showcase-empty">Пока нет разделов</p>
          <div v-else class="showcase-scroll-wrap">
            <div class="showcase-scroll">
              <button
                v-for="sec in educationSectionsVisible"
                :key="'edu-' + sec.folderKey"
                type="button"
                class="showcase-tile showcase-tile--edu"
                @click="onEducationSectionClick(sec)"
              >
                <span class="showcase-tile-icon" aria-hidden="true">
                  <ion-icon :icon="schoolOutline" />
                </span>
                <span class="showcase-tile-title">{{ sec.name }}</span>
              </button>
            </div>
          </div>
        </section>
      </template>

      <ion-modal class="showcase-program-modal" :is-open="programModalOpen" @didDismiss="closeProgramModal">
        <ion-header class="ion-no-border showcase-modal-header">
          <ion-toolbar class="showcase-modal-toolbar">
            <ion-title>{{ programModalTitle }}</ion-title>
            <ion-buttons slot="end">
              <ion-button class="showcase-modal-close" @click="closeProgramModal">Закрыть</ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="showcase-modal-content ion-padding">
          <div v-if="programModalLoading" class="showcase-modal-loading">
            <ion-spinner name="crescent" class="showcase-loading-spinner" />
          </div>
          <p v-else-if="programModalReleases.length === 0" class="showcase-empty">Нет релизов</p>
          <div v-else class="releases-row">
            <div
              v-for="release in programModalReleases"
              :key="'modal-rel-' + release.id"
              class="release-card-wrap"
            >
              <button
                type="button"
                class="release-card"
                :class="{ 'release-card--text-title': !releaseTitleIsNumericOnly(release.releaseName) }"
                :disabled="payingKey === payKey(release)"
                @click="onModalReleaseClick(release)"
              >
                <div
                  class="release-card-main"
                  :class="{
                    'release-card-main--numeric': releaseTitleIsNumericOnly(release.releaseName),
                    'release-card-main--text': !releaseTitleIsNumericOnly(release.releaseName),
                  }"
                >
                  <template v-if="releaseTitleIsNumericOnly(release.releaseName)">
                    <div class="release-card-num-line">
                      <span class="release-card-num">{{ releaseTitleTrimmed(release.releaseName) }}</span>
                      <ion-icon
                        v-if="release.canPurchase && !releaseCatalogCanOpen(release)"
                        :icon="lockClosedOutline"
                        class="release-lock release-lock--num"
                        aria-hidden="true"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <div class="release-card-text-block">
                      <span class="release-card-text-title">{{ release.releaseName }}</span>
                      <ion-icon
                        v-if="release.canPurchase && !releaseCatalogCanOpen(release)"
                        :icon="lockClosedOutline"
                        class="release-lock release-lock--text"
                        aria-hidden="true"
                      />
                    </div>
                  </template>
                </div>
                <div
                  v-if="release.canPurchase && release.priceKopecks && !releaseCatalogCanOpen(release)"
                  class="release-card-footer"
                >
                  <span class="release-card-price-pill">{{ (release.priceKopecks / 100).toFixed(0) }} ₽</span>
                  <span class="release-card-buy-label">Купить</span>
                </div>
                <div v-else-if="!releaseCatalogCanOpen(release)" class="release-card-footer release-card-footer--muted">
                  <span class="release-card-muted">Нет доступа</span>
                </div>
                <ion-spinner
                  v-if="payingKey === payKey(release)"
                  name="crescent"
                  class="release-card-spinner"
                />
              </button>
            </div>
          </div>
        </ion-content>
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  alertController,
  menuController,
  toastController
} from '@ionic/vue';
import { chatbubbleEllipsesOutline, lockClosedOutline, logInOutline, menuOutline, schoolOutline } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import axios from 'axios';
import dayjs from 'dayjs';
import { defineComponent } from 'vue';
import store from '@/store/index';
import type { ILigaEvent } from '@/model/liga-event.model';
import type { Program } from '@/model/program.model';
import type { ProgramRelease } from '@/model/programrelease.model';
import { bundledProgramImageUrl } from '@/services/logo.service';
import eventsService from '@/services/events.service';
import releasesService from '@/services/releases.service';
import subscriptionsService from '@/services/subscriptions.service';
import { fetchPublicShowcase, type PublicShowcaseProgram, type PublicShowcaseSubscription } from '@/services/showcase.service';
import {
  formatMessengerUnread,
  getMessengerUnreadTotal,
  refreshMessengerUnread,
  subscribeMessengerUnread,
} from '@/services/messenger-unread';
import { releaseEffectiveHasAccess } from '@/utils/release-catalog-privilege';
import { releaseTitleIsNumericOnly, releaseTitleTrimmed } from '@/utils/release-card-display';

export default defineComponent({
  name: 'ShowcaseHome',
  components: {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToolbar
  },
  data() {
    return {
      loading: true,
      loadError: '' as string,
      events: [] as ILigaEvent[],
      subscriptions: [] as PublicShowcaseSubscription[],
      subscribedIds: [] as number[],
      renewableSubscriptionIds: [] as number[],
      registeredEventIds: [] as number[],
      programs: [] as PublicShowcaseProgram[],
      educationSections: [] as import('@/services/showcase.service').PublicShowcaseEducationSection[],
      eventsService,
      subscriptionsService,
      menuOutline,
      schoolOutline,
      lockClosedOutline,
      logInOutline,
      chatbubbleEllipsesOutline,
      releaseTitleIsNumericOnly,
      releaseTitleTrimmed,
      programModalOpen: false,
      programModalLoading: false,
      programModalTitle: '',
      programModalCatalog: null as Program | null,
      programModalReleases: [] as ProgramRelease[],
      payingKey: null as string | null,
      desktopLayout: false,
      messengerUnreadCount: getMessengerUnreadTotal(),
      unsubscribeMessengerUnread: null as null | (() => void),
    };
  },
  computed: {
    showInlineMenuButton(): boolean {
      // На узком экране — кнопка открывает overlay-меню; на lg+ меню в split-pane.
      return !this.desktopLayout;
    },
    authenticated(): boolean {
      return store.getters.authenticated;
    },
    messengerUnreadLabel(): string {
      return formatMessengerUnread(this.messengerUnreadCount);
    },
    educationSectionsVisible(): import('@/services/showcase.service').PublicShowcaseEducationSection[] {
      return (this.educationSections || []).filter((s) => (s.videos || []).length > 0);
    }
  },
  async ionViewWillEnter() {
    await this.load();
    if (this.authenticated) void refreshMessengerUnread();
  },
  mounted() {
    this.updateDesktopLayout();
    window.addEventListener('resize', this.updateDesktopLayout);
    this.unsubscribeMessengerUnread = subscribeMessengerUnread(count => {
      this.messengerUnreadCount = count;
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateDesktopLayout);
    this.unsubscribeMessengerUnread?.();
  },
  watch: {
    authenticated(value: boolean) {
      void this.load();
      if (value) void refreshMessengerUnread();
    }
  },
  methods: {
    updateDesktopLayout() {
      this.desktopLayout = typeof window !== 'undefined' && window.matchMedia('(min-width: 992px)').matches;
    },
    programCoverUrl(shortName: string): string {
      return bundledProgramImageUrl(shortName);
    },
    releaseCount(p: PublicShowcaseProgram): number {
      if (typeof p.totalReleasesCount === 'number') return p.totalReleasesCount;
      return (p.releases || []).length;
    },
    releaseCountLabel(n: number): string {
      const v = n % 10;
      const v2 = n % 100;
      if (v2 >= 11 && v2 <= 14) return 'релизов';
      if (v === 1) return 'релиз';
      if (v >= 2 && v <= 4) return 'релиза';
      return 'релизов';
    },
    formatEventDate(iso: string | null | undefined): string {
      if (!iso) return '';
      return dayjs(iso).format('D MMMM YYYY, HH:mm');
    },
    /**
     * Длительность события в календарных днях (включительно).
     * Показываем только от 2 дней — однодневные не дублируем.
     */
    eventDurationDays(ev: ILigaEvent): number | null {
      if (!ev?.startAt || !ev?.endAt) return null;
      const start = dayjs(ev.startAt).startOf('day');
      const end = dayjs(ev.endAt).startOf('day');
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;
      const days = end.diff(start, 'day') + 1;
      return days >= 2 ? days : null;
    },
    /** Дата старта и длительность для карточки витрины, напр. «12 марта 2026, 10:00 · 3 дня». */
    eventDateMeta(ev: ILigaEvent): string {
      const date = this.formatEventDate(ev.startAt);
      const days = this.eventDurationDays(ev);
      const duration = days != null ? `${days} ${this.durationLabel(days)}` : '';
      if (date && duration) return `${date} · ${duration}`;
      return date || duration;
    },
    isSoldOut(ev: ILigaEvent): boolean {
      if (ev.maxRegistrations == null) return false;
      const count = ev.registrationsCount ?? 0;
      return count >= ev.maxRegistrations;
    },
    isEventPaid(ev: ILigaEvent): boolean {
      if (ev.isPaid) return true;
      const tiers = ev.priceTiers || [];
      if (!Array.isArray(tiers) || tiers.length === 0) return false;
      return tiers.some((t) => Number(t?.amountKopecks || 0) > 0);
    },
    isRegistered(ev: ILigaEvent): boolean {
      const eventId = String(ev.id);
      return this.registeredEventIds.some((id) => String(id) === eventId);
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const data = await fetchPublicShowcase();
        let registeredEventIds: number[] = [];
        let subscribedIds: number[] = [];
        let renewableSubscriptionIds: number[] = [];
        if (this.authenticated) {
          try {
            const registrations = await eventsService.getMyRegistrations();
            registeredEventIds = (registrations || [])
              .map((r) => r.ligaEventId)
              .filter((id): id is number => typeof id === 'number');
          } catch (e) {
            console.warn('showcase my registrations', e);
          }
          try {
            const purchases = await subscriptionsService.getMyPurchases();
            subscribedIds = (purchases || [])
              .filter((p) => p.active)
              .map((p) => p.subscriptionId)
              .filter((id): id is number => typeof id === 'number');
            renewableSubscriptionIds = (purchases || [])
              .filter((p) => p.active && !!p.expiresAt)
              .map((p) => p.subscriptionId)
              .filter((id): id is number => typeof id === 'number');
          } catch (e) {
            console.warn('showcase my subscriptions', e);
          }
        }
        this.registeredEventIds = registeredEventIds;
        this.subscribedIds = subscribedIds;
        this.renewableSubscriptionIds = renewableSubscriptionIds;
        this.events = data.events || [];
        this.subscriptions = data.subscriptions || [];
        this.programs = data.programs || [];
        this.educationSections = data.educationSections || [];
      } catch (e) {
        console.error('showcase load', e);
        this.loadError = 'Не удалось загрузить витрину. Попробуйте позже.';
      } finally {
        this.loading = false;
      }
    },
    goLogin() {
      this.$router.push('/login');
    },
    goMessenger() {
      this.$router.push({ name: 'MessengerInbox' });
    },
    durationLabel(days: number): string {
      const v = days % 10;
      const v2 = days % 100;
      if (v2 >= 11 && v2 <= 14) return 'дней';
      if (v === 1) return 'день';
      if (v >= 2 && v <= 4) return 'дня';
      return 'дней';
    },
    isSubscriptionPaid(sub: PublicShowcaseSubscription): boolean {
      const tiers = sub.priceTiers || [];
      return tiers.some((t) => Number(t?.amountKopecks || 0) > 0);
    },
    isSubscribed(sub: PublicShowcaseSubscription): boolean {
      return this.subscribedIds.some((id) => String(id) === String(sub.id));
    },
    canRenewSubscription(sub: PublicShowcaseSubscription): boolean {
      return (
        !!sub.durationDays &&
        sub.durationDays > 0 &&
        this.renewableSubscriptionIds.some((id) => String(id) === String(sub.id))
      );
    },
    onPaidSubscriptionAction(sub: PublicShowcaseSubscription) {
      const path = `/subscriptions/${sub.id}`;
      if (!this.authenticated) {
        this.$router.push({ path: '/login', query: { redirect: path, fromShowcase: '1' } });
        return;
      }
      this.$router.push(path);
    },
    async onSubscriptionCardClick(sub: PublicShowcaseSubscription) {
      if (!this.authenticated) {
        await this.toast('Войдите в личный кабинет, чтобы открыть подписку', 'warning');
        return;
      }
      this.$router.push(`/subscriptions/${sub.id}`);
    },
    async openMainMenu() {
      const opened = await menuController.open('mainMenu');
      if (!opened) await menuController.open('offlineMenu');
    },
    async toast(message: string, color = 'medium') {
      const t = await toastController.create({ message, duration: 2800, position: 'top', color });
      await t.present();
    },
    onPaidEventAction(ev: ILigaEvent) {
      const path = `/events/${ev.id}`;
      if (!this.authenticated) {
        this.$router.push({ path: '/login', query: { redirect: path, fromShowcase: '1' } });
        return;
      }
      this.$router.push(path);
    },
    async onEventCardClick(ev: ILigaEvent) {
      if (!this.authenticated) {
        await this.toast('Войдите в личный кабинет, чтобы открыть событие', 'warning');
        return;
      }
      this.$router.push(`/events/${ev.id}`);
    },
    async onProgramClick(p: PublicShowcaseProgram) {
      if (!this.authenticated) {
        void this.toast('Войдите в личный кабинет, чтобы открыть релизы программы', 'warning');
        return;
      }
      this.programModalTitle = p.programName || 'Программа';
      this.programModalCatalog = null;
      this.programModalReleases = [];
      this.programModalLoading = true;
      this.programModalOpen = true;
      try {
        const { data } = await axios.get<Program[]>('api/programs-with-releases');
        const catalog = (data || []).find((prog) => prog.id === p.id) || null;
        this.programModalCatalog = catalog;
        this.programModalTitle = catalog?.programName || p.programName || 'Программа';
        this.programModalReleases = [...(catalog?.fullReleases || [])].sort((a, b) =>
          (a.releaseName || '').localeCompare(b.releaseName || '', 'ru', { numeric: true })
        );
      } catch (e) {
        console.error('program modal catalog', e);
        await this.toast('Не удалось загрузить релизы', 'danger');
        this.closeProgramModal();
      } finally {
        this.programModalLoading = false;
      }
    },
    closeProgramModal() {
      this.programModalOpen = false;
      this.programModalLoading = false;
      this.programModalCatalog = null;
      this.programModalReleases = [];
      this.payingKey = null;
    },
    releaseCatalogCanOpen(release: ProgramRelease): boolean {
      return releaseEffectiveHasAccess(release, store.getters.account);
    },
    payKey(release: ProgramRelease): string {
      return `${this.programModalCatalog?.id ?? 'p'}-${release.id}`;
    },
    isValidEmail(s: string | null | undefined): boolean {
      if (s == null || typeof s !== 'string') return false;
      const t = s.trim();
      const at = t.indexOf('@');
      return at > 0 && at < t.length - 1 && t.indexOf('.', at + 1) > at + 1;
    },
    async onModalReleaseClick(release: ProgramRelease) {
      if (this.releaseCatalogCanOpen(release)) {
        await this.openRelease(release);
        return;
      }
      if (release.canPurchase) {
        await this.goPayRelease(release);
        return;
      }
      await this.toast('Нет доступа к этому релизу', 'warning');
    },
    async openRelease(release: ProgramRelease) {
      const program = this.programModalCatalog;
      if (!program || !this.releaseCatalogCanOpen(release)) {
        await this.toast('Нет доступа к этому релизу', 'warning');
        return;
      }
      await Preferences.set({
        key: 'selectedProgram',
        value: program.programName || ''
      });
      await Preferences.set({
        key: 'releaseMeta',
        value: JSON.stringify({
          programName: program.programName,
          programShortName: program.shortName,
          release: release.releaseName,
          releaseVisible: String(release.visible ?? true),
          releaseId: String(release.id),
          contentSource: release.contentSource || 'SERVER'
        })
      });
      this.closeProgramModal();
      this.$router.push({ name: 'ReleasePage' });
    },
    async goPayRelease(release: ProgramRelease) {
      if (!release.id) return;
      const tiers = release.priceTiers?.filter((t) => (t.amountKopecks ?? 0) > 0) ?? [];
      let priceTierId: number | undefined;
      if (tiers.length >= 2) {
        const alert = await alertController.create({
          header: 'Выберите тариф',
          inputs: tiers.map((t) => ({
            type: 'radio' as const,
            label: `${t.name} — ${((t.amountKopecks ?? 0) / 100).toFixed(0)} ₽`,
            value: String(t.id)
          })),
          buttons: [
            { text: 'Отмена', role: 'cancel' },
            {
              text: 'Далее',
              handler: (value) => {
                if (!value) return false;
                priceTierId = Number(value);
                void this.continueReleasePurchase(release, priceTierId);
                return true;
              }
            }
          ]
        });
        await alert.present();
        return;
      }
      if (tiers.length === 1 && tiers[0].id != null) {
        priceTierId = tiers[0].id;
      }
      await this.continueReleasePurchase(release, priceTierId);
    },
    async continueReleasePurchase(release: ProgramRelease, priceTierId?: number) {
      if (!release.id) return;
      const acct = store.getters.account as { email?: string } | null | undefined;
      const userEmail = acct?.email?.trim();
      if (!this.isValidEmail(userEmail)) {
        const alert = await alertController.create({
          header: 'Email для чека',
          message: 'Укажите электронную почту, на которую будет направлен чек после оплаты.',
          inputs: [{ name: 'email', type: 'email', placeholder: 'example@mail.ru', attributes: { required: true } }],
          cssClass: 'liga-email-receipt-alert',
          buttons: [
            { text: 'Отмена', role: 'cancel' },
            {
              text: 'Оплатить',
              handler: (data: { email?: string }) => {
                const entered = data?.email?.trim();
                if (!this.isValidEmail(entered)) {
                  toastController
                    .create({ message: 'Укажите корректный email для чека.', color: 'warning', duration: 4000 })
                    .then((t) => t.present());
                  return false;
                }
                void this.executeReleasePurchase(release, entered as string, priceTierId);
                return true;
              }
            }
          ]
        });
        await alert.present();
        return;
      }
      const emailForReceipt = userEmail as string;
      await this.toast(`Чек будет направлен на ${emailForReceipt}`, 'primary');
      await this.executeReleasePurchase(release, emailForReceipt, priceTierId);
    },
    async executeReleasePurchase(release: ProgramRelease, customerEmail: string, priceTierId?: number) {
      if (!release.id) return;
      const key = this.payKey(release);
      this.payingKey = key;
      try {
        const { redirectUrl } = await releasesService.createReleasePurchase(release.id, customerEmail, priceTierId);
        if (redirectUrl) {
          if (Capacitor.isNativePlatform()) window.open(redirectUrl, '_system');
          else window.location.href = redirectUrl;
        }
      } catch (e: any) {
        const data = e?.response?.data ?? e?.data;
        const msg =
          (typeof data?.message === 'string' && !String(data.message).startsWith('error.') ? data.message : null) ||
          e?.message ||
          'Ошибка оплаты';
        await this.toast(msg, 'warning');
      } finally {
        this.payingKey = null;
      }
    },
    async onEducationSectionClick(sec: import('@/services/showcase.service').PublicShowcaseEducationSection) {
      if (!this.authenticated) {
        await this.toast('Войдите в личный кабинет, чтобы открыть раздел', 'warning');
        return;
      }
      try {
        const { data: sections } = await axios.get<Array<{ folderKey: string }>>('api/admin/education/sections');
        const keys = new Set((sections || []).map((s) => s.folderKey));
        if (!keys.has(sec.folderKey)) {
          await this.toast('Нет доступа к этому разделу', 'warning');
          return;
        }
        await this.$router.push({
          path: '/skills',
          query: { section: sec.folderKey }
        });
      } catch (e) {
        console.error('education access', e);
        await this.toast('Не удалось проверить доступ', 'danger');
      }
    }
  }
});
</script>

<style scoped>
.showcase-page-header,
.showcase-float-toolbar {
  --background: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden;
  min-height: 0;
}

.showcase-float-toolbar {
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: env(safe-area-inset-top);
  --min-height: 56px;
  --border-width: 0;
}

.showcase-float-toolbar :deep(.toolbar-container),
.showcase-float-toolbar :deep(.toolbar-content) {
  overflow: hidden !important;
}

.showcase-float-bar {
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 56px;
}

.showcase-float-btn {
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: var(--liga-chrome-text, #fff);
  flex-shrink: 0;
  position: relative;
}

.showcase-float-btn ion-icon {
  font-size: 22px;
}

.showcase-float-btn .ios-unread-badge--on-icon {
  right: 1px;
  top: 1px;
}

.showcase-title-pill {
  height: 44px;
  min-height: 44px;
  max-height: 44px;
  padding: 4px 12px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.showcase-title-pill .liga-float-title-text {
  font-size: 0.88rem;
  font-weight: 700;
}

.showcase-title-pill .liga-float-title-sub {
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 0.65rem;
  margin-top: 2px;
}

.showcase-accent-btn {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-shade);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange);
  --color: #fff;
  --box-shadow: 0 6px 18px rgba(235, 89, 82, 0.38);
}

.showcase-state {
  margin: 1.25rem 16px 0;
  padding: 1.25rem 1.1rem;
  border-radius: 18px;
  background: var(--liga-float-glass-bg);
  border: 1px solid var(--liga-float-glass-border);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  text-align: center;
}

.showcase-loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 2.5rem 1.5rem;
}

.showcase-loading-spinner {
  --color: var(--ion-color-orange);
  color: var(--ion-color-orange);
}

.showcase-loading-text {
  margin: 0;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.72));
  font-size: 0.95rem;
}

.showcase-section {
  margin-bottom: 1.85rem;
  padding: 0 4px;
}

.showcase-section--first {
  margin-top: 0.35rem;
}

.showcase-section--last {
  margin-bottom: max(2.25rem, calc(env(safe-area-inset-bottom) + 1.25rem));
}

.showcase-section-head {
  display: flex;
  align-items: center;
  margin: 0 14px 14px;
}

.showcase-section-title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--liga-chrome-text, #fff);
  letter-spacing: -0.01em;
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
}

.showcase-empty {
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.58));
  margin: 0 16px 12px;
  padding: 12px 14px;
  font-size: 0.9rem;
  border-radius: 14px;
  background: var(--liga-float-glass-bg);
  border: 1px solid var(--liga-float-glass-border);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
}

.showcase-error {
  color: rgba(255, 190, 190, 0.96);
}

.showcase-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 6px;
  mask-image: linear-gradient(to right, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%);
}

.showcase-scroll {
  display: flex;
  flex-direction: row;
  gap: 14px;
  padding: 0 14px 8px;
  width: max-content;
}

.showcase-tile {
  flex: 0 0 auto;
  width: 200px;
  margin: 0;
  padding: 18px 16px;
  border-radius: 18px;
  text-align: left;
  cursor: pointer;
  color: var(--liga-chrome-text, #fff);
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.showcase-tile:active {
  transform: scale(0.98);
}

.showcase-tile--edu {
  min-height: 132px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.showcase-tile--program {
  width: 216px;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.showcase-tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  margin-bottom: 12px;
  color: var(--ion-color-orange-tint);
  background: rgba(235, 89, 82, 0.18);
  border: 1px solid rgba(235, 89, 82, 0.38);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.showcase-tile-icon ion-icon {
  font-size: 24px;
}

.showcase-tile-title {
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--liga-chrome-text, #fff);
}

.showcase-tile-meta {
  margin-top: 8px;
  font-size: 0.75rem;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.55));
  font-weight: 500;
}

.showcase-program-cover {
  height: 108px;
  background: rgba(0, 0, 0, 0.28);
  border-bottom: 1px solid var(--liga-float-glass-border);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.showcase-cover-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.35));
}

.showcase-release-count {
  margin: 10px 16px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ion-color-orange-tint);
  letter-spacing: 0.02em;
}

.showcase-tile--program .showcase-tile-title {
  padding: 0 16px;
  margin-top: 12px;
}

.showcase-event-card,
.showcase-sub-card {
  flex: 0 0 auto;
  width: 272px;
  margin: 0;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg-strong);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.showcase-event-card:active {
  transform: scale(0.99);
}

.showcase-event-image-wrap {
  position: relative;
  height: 168px;
  overflow: hidden;
}

.showcase-event-image-wrap--no-logo {
  background: linear-gradient(150deg, rgba(48, 48, 54, 0.75) 0%, rgba(18, 18, 22, 0.95) 100%);
}

.showcase-event-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-event-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 14px 14px;
  background: linear-gradient(transparent 0%, rgba(12, 12, 16, 0.78) 100%);
}

.showcase-event-overlay--no-logo {
  background: linear-gradient(180deg, rgba(20, 20, 24, 0.35) 0%, rgba(12, 12, 16, 0.82) 100%);
}

.showcase-event-title {
  margin: 0 0 4px 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}

.showcase-event-meta,
.showcase-event-loc {
  margin: 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.35;
}

.showcase-event-badges {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.showcase-badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.showcase-badge--paid {
  background: rgba(255, 196, 9, 0.95);
  color: #1a1a1a;
}

.showcase-badge--soldout {
  background: rgba(220, 47, 2, 0.95);
  color: #fff;
}

.showcase-badge--registered {
  background: rgba(45, 211, 111, 0.9);
  color: #1a1a1a;
}

.showcase-event-actions {
  padding: 12px 14px 14px;
  background: rgba(0, 0, 0, 0.22);
  border-top: 1px solid var(--liga-float-glass-border);
}

.showcase-buy-btn {
  margin: 0;
  --border-radius: 999px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.01em;
}

.showcase-program-modal {
  --background: var(--liga-page-bg);
  --border-radius: 16px;
}

.showcase-modal-header :deep(ion-toolbar.showcase-modal-toolbar),
.showcase-modal-toolbar {
  --background: var(--liga-float-glass-bg-strong);
  --color: var(--liga-chrome-text, #fff);
  --border-width: 0;
  border-bottom: 1px solid var(--liga-float-glass-border);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
}

.showcase-modal-toolbar ion-title,
.showcase-modal-header :deep(ion-title) {
  color: var(--liga-chrome-text, #fff);
}

.showcase-modal-close {
  --color: var(--ion-color-orange);
  font-weight: 650;
  text-transform: none;
}

.showcase-modal-content {
  --background: var(--liga-page-bg);
  background: var(--liga-page-bg);
}

.showcase-modal-loading {
  display: flex;
  justify-content: center;
  padding: 2.5rem 0;
}

.releases-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 4px 0 10px;
}

.release-card-wrap {
  position: relative;
  flex: 0 0 auto;
  width: 158px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.release-card {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 118px;
  border-radius: 16px;
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  color: var(--liga-chrome-text, #fff);
  text-align: center;
  padding: 12px 10px 12px;
  font-family: 'Montserrat', system-ui, sans-serif;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.release-card--text-title {
  text-align: center;
}

.release-card:not(:disabled):active {
  transform: scale(0.98);
}

.release-card:disabled {
  opacity: 0.72;
}

.release-card-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.25rem;
}

.release-card-main--numeric {
  min-height: 4rem;
  padding: 2px 6px 0;
}

.release-card-num-line {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
}

.release-card-num {
  font-size: clamp(2rem, 14vw, 3.35rem);
  font-weight: 800;
  line-height: 0.95;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  color: var(--liga-chrome-text, rgba(255, 255, 255, 0.98));
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.2);
}

html.liga-theme-light .release-card-num {
  text-shadow: none;
}

.release-card-main--text {
  min-height: 3.1rem;
  padding: 2px 30px 2px 8px;
  align-items: center;
}

.release-card-text-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-height: 5.2em;
}

.release-card-text-title {
  font-size: clamp(0.7rem, 3.4vw, 0.92rem);
  font-weight: 700;
  line-height: 1.22;
  text-align: center;
  word-break: break-word;
  overflow-wrap: anywhere;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--liga-chrome-text, rgba(255, 255, 255, 0.96));
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

html.liga-theme-light .release-card-text-title {
  text-shadow: none;
}

.release-lock {
  color: var(--ion-color-orange-tint);
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
}

.release-lock--num,
.release-lock--text {
  font-size: 1.05rem;
}

.release-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid var(--liga-float-glass-border);
}

.release-card-footer--muted {
  border-top-color: rgba(255, 255, 255, 0.08);
  justify-content: center;
}

.release-card-price-pill {
  font-size: 0.8rem;
  font-weight: 800;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(235, 89, 82, 0.22);
  color: #ffd4b8;
  border: 1px solid rgba(235, 89, 82, 0.45);
}

.release-card-buy-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.release-card-muted {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.48);
}

.release-card-spinner {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  color: #fff;
}
</style>

<style>
/* Модалка телепортируется вне scoped-дерева */
ion-modal.showcase-program-modal {
  --background: #121214;
}

ion-modal.showcase-program-modal .showcase-modal-toolbar {
  --background: rgba(28, 28, 32, 0.85);
  --color: #fff;
  --border-width: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

ion-modal.showcase-program-modal .showcase-modal-close {
  --color: var(--ion-color-orange-tint, #eb5952);
  font-weight: 650;
  text-transform: none;
}

ion-modal.showcase-program-modal .showcase-modal-content {
  --background: linear-gradient(315deg, #2d3436 0%, #000000 74%);
}

ion-modal.showcase-program-modal .release-card {
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(28, 28, 32, 0.62);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
}

ion-modal.showcase-program-modal .release-lock {
  color: var(--ion-color-orange-tint, #eb5952);
}

ion-modal.showcase-program-modal .release-card-price-pill {
  background: rgba(235, 89, 82, 0.22);
  color: #ffd4b8;
  border: 1px solid rgba(235, 89, 82, 0.45);
}
</style>
